
import { MeetingNote } from "../types";

export async function fetchSpaces(accessToken: string): Promise<{name: string, displayName: string, type: string}[]> {
  const spacesResponse = await fetch('https://chat.googleapis.com/v1/spaces?pageSize=1000', {
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  if (!spacesResponse.ok) {
    throw new Error(`Failed to fetch spaces: ${await spacesResponse.text()}`);
  }

  const spacesData = await spacesResponse.json();
  console.log("Fetched spaces data:", spacesData);
  if (!spacesData.spaces) return [];

  return spacesData.spaces.map((s: any) => ({
    name: s.name,
    displayName: s.displayName || (s.spaceType === 'DIRECT_MESSAGE' || s.type === 'DM' ? 'Direct Message' : 'Unnamed Space'),
    type: s.spaceType || s.type
  }));
}

export async function sendMessageToSpace(accessToken: string, spaceName: string, text: string): Promise<void> {
  const url = `https://chat.googleapis.com/v1/${spaceName}/messages`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ text })
  });

  if (!response.ok) {
    throw new Error(`Failed to send message to space ${spaceName}: ${await response.text()}`);
  }
}

export async function sendDirectMessage(accessToken: string, email: string, text: string): Promise<void> {
  // 1. Setup a space for the direct message
  const setupUrl = `https://chat.googleapis.com/v1/spaces:setup`;
  const setupResponse = await fetch(setupUrl, {
    method: 'POST',
    headers: { 
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      space: {
        spaceType: "DIRECT_MESSAGE",
        singleUserBotDm: false
      },
      memberships: [
        {
          member: {
            name: `users/${email}`,
            type: "HUMAN"
          }
        }
      ]
    })
  });

  if (!setupResponse.ok) {
    throw new Error(`Failed to setup DM with ${email}: ${await setupResponse.text()}`);
  }

  const spaceData = await setupResponse.json();
  const spaceName = spaceData.name;

  // 2. Send the message to the created/found space
  await sendMessageToSpace(accessToken, spaceName, text);
}

export async function fetchChatMessages(accessToken: string, days: number = 7, currentUserName: string = '', userSub: string = ''): Promise<MeetingNote[]> {
  const lookbackDate = new Date();
  lookbackDate.setDate(lookbackDate.getDate() - days);
  const filterDateStr = lookbackDate.toISOString();
  
  // 1. List Spaces (fetch up to 1000 to ensure we get the active ones)
  const spacesResponse = await fetch('https://chat.googleapis.com/v1/spaces?pageSize=1000', {
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  if (!spacesResponse.ok) {
    const errText = await spacesResponse.text();
    // Throwing error allows App.tsx to handle re-authorization logic for 403 Insufficient Scope
    throw new Error(`Chat API error (${spacesResponse.status}): ${errText}`);
  }

  const spacesData = await spacesResponse.json();
  if (!spacesData.spaces || spacesData.spaces.length === 0) return [];

  // Sort spaces by lastActiveTime or updateTime (newest first)
  const sortedSpaces = spacesData.spaces.sort((a: any, b: any) => {
    const timeA = new Date(a.lastActiveTime || a.updateTime || a.createTime || 0).getTime();
    const timeB = new Date(b.lastActiveTime || b.updateTime || b.createTime || 0).getTime();
    return timeB - timeA;
  });

  // 2. Fetch messages from up to 10 most recently active spaces (reduced to save API tokens)
  const spacesToQuery = sortedSpaces.slice(0, 10);

  const results: (MeetingNote | null)[] = [];
  const chunkSize = 5; // Process in chunks to avoid rate limits

  const fetchMemberships = async (spaceName: string) => {
    let userMap: Record<string, string> = {};
    try {
      let pageToken = '';
      do {
        const url = `https://chat.googleapis.com/v1/${spaceName}/members?pageSize=1000${pageToken ? `&pageToken=${pageToken}` : ''}`;
        const memRes = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (!memRes.ok) {
          if (memRes.status === 403) {
            console.warn(`Chat API error (403): Missing memberships scope for ${spaceName}.`);
          }
          break;
        }
        const memData = await memRes.json();
        if (memData.memberships) {
          memData.memberships.forEach((m: any) => {
            if (m.member?.name && m.member?.displayName) {
              userMap[m.member.name] = m.member.displayName;
            }
          });
        }
        pageToken = memData.nextPageToken;
      } while (pageToken);
    } catch (e) {
      console.warn("Failed to fetch memberships for", spaceName);
    }
    return userMap;
  };

  const resolveUserNamesBatch = async (userIds: string[], userMap: Record<string, string>) => {
    const idsToFetch = userIds.filter(id => !userMap[id]);
    if (idsToFetch.length === 0) return;

    for (let i = 0; i < idsToFetch.length; i += 50) {
      const chunk = idsToFetch.slice(i, i + 50);
      const params = new URLSearchParams();
      params.append('personFields', 'names');
      chunk.forEach(id => {
        params.append('resourceNames', id.replace('users/', 'people/'));
      });

      try {
        const res = await fetch(`https://people.googleapis.com/v1/people:batchGet?${params.toString()}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.responses) {
            data.responses.forEach((r: any) => {
              const originalId = r.requestedResourceName.replace('people/', 'users/');
              if (r.httpStatusCode === 200 && r.person?.names?.[0]?.displayName) {
                userMap[originalId] = r.person.names[0].displayName;
              }
            });
          }
        }
      } catch (e) {
        console.warn("Batch get people failed", e);
      }
    }
  };

  for (let i = 0; i < spacesToQuery.length; i += chunkSize) {
    const chunk = spacesToQuery.slice(i, i + chunkSize);
    const messagePromises = chunk.map(async (space: any) => {
      try {
        // Fetch recent messages using filter
        const filter = encodeURIComponent(`create_time > "${filterDateStr}"`);
        // We use a smaller pageSize to limit token consumption when summarizing
        const messagesUrl = `https://chat.googleapis.com/v1/${space.name}/messages?pageSize=50&filter=${filter}`;
        const msgRes = await fetch(messagesUrl, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });

        if (!msgRes.ok) {
            // Fallback if filter is not supported or fails
            const fallbackUrl = `https://chat.googleapis.com/v1/${space.name}/messages?pageSize=50&orderBy=create_time%20desc`;
            const fbRes = await fetch(fallbackUrl, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (!fbRes.ok) return null;
            const fbData = await fbRes.json();
            if (!fbData.messages) return null;
            
            const recentMessages = fbData.messages.filter((m: any) => {
                const createTime = new Date(m.createTime);
                return createTime > lookbackDate;
            });
            if (recentMessages.length === 0) return null;
            recentMessages.reverse(); // chronological
            
            const userMap = await fetchMemberships(space.name);

            // Resolve all unknown senders in parallel
            const unknownSenders = new Set<string>();
            recentMessages.forEach((m: any) => {
              if (m.sender?.name && !m.sender?.displayName && !userMap[m.sender.name] && m.sender.name !== `users/${userSub}` && m.sender.type !== 'BOT') {
                unknownSenders.add(m.sender.name);
              }
            });
            
            await resolveUserNamesBatch(Array.from(unknownSenders), userMap);

            const content = recentMessages
              .map((m: any) => {
                const isCurrentUser = m.sender?.name === `users/${userSub}`;
                let senderName = isCurrentUser ? currentUserName : (m.sender?.displayName || userMap[m.sender?.name]);
                if (!senderName && m.sender?.type === 'BOT') senderName = 'Bot';
                if (!senderName) senderName = `Unknown (${m.sender?.name || JSON.stringify(m.sender)})`;
                
                const spaceId = m.name?.split('/')[1];
                const messageId = m.name?.split('/')[3];
                const msgUrl = spaceId && messageId ? `https://mail.google.com/chat/u/0/#chat/space/${spaceId}/${messageId}` : '';
                
                return msgUrl ? `[${senderName}](${msgUrl}): ${m.text}` : `${senderName}: ${m.text}`;
              })
              .join('\n\n');

            let spaceName = space.displayName;
            if (!spaceName) {
              const uniqueSenders = Array.from(new Set(recentMessages.map((m: any) => {
                const isCurrentUser = m.sender?.name === `users/${userSub}`;
                if (isCurrentUser) return null;
                let name = m.sender?.displayName || userMap[m.sender?.name];
                if (!name && m.sender?.type === 'BOT') return 'Bot';
                return name || m.sender?.name;
              }).filter((name: any) => name && !name.startsWith('Unknown') && name !== currentUserName)));
              spaceName = uniqueSenders.length > 0 ? uniqueSenders.join(', ') : 'Unnamed Space';
            }

            return {
              id: space.name,
              title: `Chat Log: ${spaceName}`,
              date: new Date(recentMessages[0].createTime).toISOString().split('T')[0],
              content: content.slice(0, 5000),
              source: 'chat' as const,
              url: `https://mail.google.com/chat/u/0/#chat/space/${space.name.split('/')[1]}`
            };
        }

        const msgData = await msgRes.json();
        if (!msgData.messages || msgData.messages.length === 0) return null;

        // The API returns messages in ascending order (oldest to newest) when using filter
        const recentMessages = msgData.messages;
        const userMap = await fetchMemberships(space.name);

        // Resolve all unknown senders in parallel
        const unknownSenders = new Set<string>();
        recentMessages.forEach((m: any) => {
          if (m.sender?.name && !m.sender?.displayName && !userMap[m.sender.name] && m.sender.name !== `users/${userSub}` && m.sender.type !== 'BOT') {
            unknownSenders.add(m.sender.name);
          }
        });
        
        await resolveUserNamesBatch(Array.from(unknownSenders), userMap);

        const content = recentMessages
          .map((m: any) => {
            const isCurrentUser = m.sender?.name === `users/${userSub}`;
            let senderName = isCurrentUser ? currentUserName : (m.sender?.displayName || userMap[m.sender?.name]);
            if (!senderName && m.sender?.type === 'BOT') senderName = 'Bot';
            if (!senderName) senderName = `Unknown (${m.sender?.name || JSON.stringify(m.sender)})`;
            
            const spaceId = m.name?.split('/')[1];
            const messageId = m.name?.split('/')[3];
            const msgUrl = spaceId && messageId ? `https://mail.google.com/chat/u/0/#chat/space/${spaceId}/${messageId}` : '';
            
            return msgUrl ? `[${senderName}](${msgUrl}): ${m.text}` : `${senderName}: ${m.text}`;
          })
          .join('\n\n');

        let spaceName = space.displayName;
        if (!spaceName) {
          const uniqueSenders = Array.from(new Set(recentMessages.map((m: any) => {
            const isCurrentUser = m.sender?.name === `users/${userSub}`;
            if (isCurrentUser) return null;
            let name = m.sender?.displayName || userMap[m.sender?.name];
            if (!name && m.sender?.type === 'BOT') return 'Bot';
            return name || m.sender?.name;
          }).filter((name: any) => name && !name.startsWith('Unknown') && name !== currentUserName)));
          spaceName = uniqueSenders.length > 0 ? uniqueSenders.join(', ') : 'Unnamed Space';
        }

        return {
          id: space.name,
          title: `Chat Log: ${spaceName}`,
          date: new Date(recentMessages[0].createTime).toISOString().split('T')[0],
          content: content.slice(0, 5000),
          source: 'chat' as const,
          url: `https://mail.google.com/chat/u/0/#chat/space/${space.name.split('/')[1]}`
        };
      } catch (e) {
        console.warn(`Failed to fetch messages for space ${space.name}`, e);
        return null;
      }
    });

    const chunkResults = await Promise.all(messagePromises);
    results.push(...chunkResults);
  }

  return results.filter((n): n is MeetingNote => n !== null);
}
